import React from "react";
import PatternOverlay from "@/components/PatternOverlay";

export default function OurStoryPage() {
  return (
    <div className="pattern-bg w-full" data-testid="our-story-page">
      <PatternOverlay opacity={0.09} size={520} />
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-28">
        <div className="text-xs uppercase tracking-[0.32em] text-primary">About us</div>
        <h1 className="font-serif text-5xl md:text-7xl mt-4 tracking-tight leading-[1.02]">
          Where scent becomes <span className="italic text-primary">signature</span>.
        </h1>

        {/* ASH callout */}
        <section className="mt-16 border-y border-border py-10 md:py-14">
          <div className="text-xs uppercase tracking-[0.32em] text-primary">Ash · æ</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">
            The first letter in our name.
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            The near-open front unrounded vowel — International Phonetic Alphabet
            number <span className="text-primary">325</span>. In English phonology
            it is pronounced as the <span className="italic">a</span> in{" "}
            <span className="italic">cat</span> [khæt]. Which makes{" "}
            <span className="normal-case">æxènts</span> pronounced{" "}
            <span className="italic text-primary">accents</span>.
          </p>
        </section>

        {[
          {
            eyebrow: "Our philosophy",
            title: "Scent as a form of identity.",
            body: [
              "We see fragrance not as an accessory, but as an extension of self. Every note, every accord, every composition is designed to help you express who you are and how you feel. æxènts was founded on the idea that scent should be personal, intentional, and transformative — a signature authored by the wearer, not assigned by the bottle.",
              "This philosophy shapes everything we create. Our perfumes are formulated to be worn alone or layered, giving you the freedom to build a scent that evolves with your mood, your moment, and your individuality. Through layering, your fragrance becomes a living expression of identity — fluid, memorable, unmistakably yours.",
            ],
          },
          {
            eyebrow: "The art of layering",
            title: "Your signature, your way.",
            body: [
              "Layering is the heart of æxènts. Each fragrance is meticulously crafted to harmonise with the others, unlocking endless combinations that allow you to design your own olfactory signature.",
            ],
          },
        ].map((s) => (
          <section key={s.eyebrow} className="mt-16">
            <div className="text-xs uppercase tracking-[0.32em] text-primary">{s.eyebrow}</div>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 tracking-tight leading-tight">
              {s.title}
            </h2>
            {s.body.map((p, i) => (
              <p key={i} className="mt-6 text-muted-foreground leading-relaxed">
                {p}
              </p>
            ))}
          </section>
        ))}

        {/* Layering palette */}
        <section className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { name: "Blæk", desc: "deep, dark, magnetic" },
            { name: "Vétiver", desc: "bright, effervescent, uplifting" },
            { name: "Ivoire", desc: "soft, powdery, serene" },
          ].map((n) => (
            <div key={n.name} className="border border-border p-6" data-testid={`palette-${n.name.toLowerCase()}`}>
              <div className="font-serif text-3xl text-primary">{n.name}</div>
              <div className="mt-2 text-sm text-muted-foreground italic">{n.desc}</div>
            </div>
          ))}
        </section>

        <p className="mt-10 text-muted-foreground leading-relaxed">
          Together, they form an olfactory palette that empowers creativity and
          self-expression. With <span className="normal-case">æxènts</span>, you
          don't choose a single scent — you compose one. This modular approach
          also supports conscious luxury: by creating multiple expressions from
          fewer bottles, you reduce packaging waste and embrace a more
          sustainable way to experience fragrance.
        </p>

        {[
          {
            eyebrow: "Our craft",
            title: "Purity, precision, provenance.",
            body: [
              "Every æxènts perfume begins with the world's finest ingredients, sourced for their clarity, complexity, and emotional resonance. Our compositions are crafted with precision, ensuring each spray transports you to a place of inspiration, memory, or discovery.",
              "Our bottles reflect our design ethos: minimalist, sculptural, and intentional. Their clean lines and rich tones mirror the purity of our scents and the depth of the experiences within.",
            ],
          },
          {
            eyebrow: "Our story",
            title: "Born from exploration.",
            body: [
              "æxènts was conceived through a journey across cultures, landscapes, and human experiences. Our founder's travels revealed a universal truth: scent is a language. It communicates emotion, memory, and identity without a single word. æxènts translates this language into a collection of perfumes designed to help you express your own story.",
            ],
          },
          {
            eyebrow: "A movement",
            title: "Modern perfumery, reimagined.",
            body: [
              "æxènts is more than a fragrance house — it is a celebration of individuality. A space where scent becomes signature. A movement that invites you to explore, layer, and define who you are through the art of olfactory design.",
              "With æxènts, your fragrance is crafted by you, for you.",
            ],
          },
        ].map((s) => (
          <section key={s.eyebrow} className="mt-16">
            <div className="text-xs uppercase tracking-[0.32em] text-primary">{s.eyebrow}</div>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 tracking-tight leading-tight">
              {s.title}
            </h2>
            {s.body.map((p, i) => (
              <p key={i} className="mt-6 text-muted-foreground leading-relaxed">
                {p}
              </p>
            ))}
          </section>
        ))}

        <div className="mt-20 text-center font-serif text-3xl md:text-4xl italic text-primary">
          Your scent is your signature.
        </div>
      </div>
    </div>
  );
}
